import React, { useState, useMemo } from 'react';
import { useAnalysisContext } from '../../../../context/AnalysisContext';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Download, Network, Zap, CheckCircle2, TrendingUp, AlertCircle } from 'lucide-react';
import TargetDetailsModal from '../components/TargetDetailsModal';
import { RankingCategoryBadge } from '../components/RankingCategoryBadge';
import { RankingCategory } from '../../../../../../types';

/**
 * Table Tab - Sortable, filterable results table
 *
 * Features:
 * - Sortable columns (supports dual ranking)
 * - Category filtering (CONSENSUS, TOP_EXPRESSION, etc.)
 * - View mode toggle (Expression | Network | Both)
 * - Search/filter
 * - Export visible rows
 * - Pagination
 * - Rank-based display
 * - Clickable rows for detailed analysis
 */

type SortField = 'lncrna' | 'rank' | 'expression_rank' | 'priority_rank' | 'rank_difference';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'expression' | 'network' | 'both';

export const TableTab: React.FC = () => {
  const { analysisResult } = useAnalysisContext();
  // Default sort by rank in ascending order (1, 2, 3...)
  const [sortField, setSortField] = useState<SortField>('rank');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);

  // Dual ranking states
  const [viewMode, setViewMode] = useState<ViewMode>('both');
  const [categoryFilter, setCategoryFilter] = useState<RankingCategory | null>(null);

  const rowsPerPage = 25;

  // Check if we have dual ranking data
  const hasDualRanking = analysisResult?.targets?.[0]?.priority_rank !== undefined;

  // ========================================
  // SORTING AND FILTERING
  // ========================================

  const sortedAndFilteredData = useMemo(() => {
    // Use dual ranking targets if available, otherwise fall back to lncrna_results
    const sourceData = hasDualRanking ? analysisResult?.targets : analysisResult?.lncrna_results;

    if (!sourceData || sourceData.length === 0) {
      return [];
    }

    let data = [...sourceData];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter((row: any) =>
        row.lncrna.toLowerCase().includes(query)
      );
    }

    // Filter by category (dual ranking only)
    if (categoryFilter && hasDualRanking) {
      data = data.filter((row: any) => row.ranking_category === categoryFilter);
    }

    // Sort with multi-level sorting
    // Always prioritize GRN genes first (hidden from user), then sort by user's chosen field
    data.sort((a: any, b: any) => {
      // First priority: GRN genes always come first (hidden sorting)
      const aHasGRN = a.in_grn ? 1 : 0;
      const bHasGRN = b.in_grn ? 1 : 0;
      if (aHasGRN !== bHasGRN) {
        return bHasGRN - aHasGRN; // GRN genes (1) before non-GRN (0)
      }

      // Second priority: User's selected sort field
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle boolean values
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      let comparison = 0;
      if (sortDirection === 'asc') {
        comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        comparison = aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }

      // Third priority: rank (ascending) if selected field values are equal
      if (comparison === 0 && sortField !== 'rank') {
        return (a.rank || 999) - (b.rank || 999);
      }

      return comparison;
    });

    return data;
  }, [hasDualRanking, analysisResult?.targets, analysisResult?.lncrna_results, sortField, sortDirection, searchQuery, categoryFilter]);

  // ========================================
  // PAGINATION
  // ========================================

  const totalPages = Math.ceil(sortedAndFilteredData.length / rowsPerPage);
  const paginatedData = sortedAndFilteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // ========================================
  // HANDLERS
  // ========================================

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    // Create CSV content - simplified to match table display
    const headers = ['lncRNA', 'Rank'];
    const rows = sortedAndFilteredData.map((row: any) => [
      row.lncrna,
      row.rank ?? 'N/A',
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analysis_results.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ========================================
  // SORT ICON
  // ========================================

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 opacity-30" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-4 h-4 text-[var(--origin-blue-500)]" />
    ) : (
      <ArrowDown className="w-4 h-4 text-[var(--origin-blue-500)]" />
    );
  };

  // ========================================
  // MAIN RENDER
  // ========================================

  if (sortedAndFilteredData.length === 0 && !searchQuery) {
    return (
      <div className="p-8 text-center text-[var(--text-secondary)]">
        No table data available
      </div>
    );
  }

  return (
    <>
      {/* Target Details Modal */}
      {selectedTarget && (
        <TargetDetailsModal
          targetGene={selectedTarget}
          onClose={() => setSelectedTarget(null)}
        />
      )}

      <div className="p-6 max-w-full">
        {/* Header with Search, Filter, and Export */}
        <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
            <input
              type="text"
              placeholder="Search lncRNAs..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="origin-input pl-10 w-full"
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={handleExportCSV}
            className="origin-btn origin-btn-secondary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Dual Ranking Controls (only if dual ranking data available) */}
      {hasDualRanking && (
        <>
          {/* View Mode Toggle */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-[var(--text-secondary)]">View:</span>
            <div className="inline-flex rounded-lg border border-[var(--border-primary)] bg-[var(--bg-secondary)] p-1">
              <button
                onClick={() => setViewMode('expression')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'expression'
                    ? 'bg-[var(--origin-blue-500)] text-white'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                <TrendingUp className="w-4 h-4 inline mr-1" />
                Expression
              </button>
              <button
                onClick={() => setViewMode('network')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'network'
                    ? 'bg-purple-500 text-white'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                <Network className="w-4 h-4 inline mr-1" />
                Network
              </button>
              <button
                onClick={() => setViewMode('both')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'both'
                    ? 'bg-[var(--origin-blue-500)] text-white'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                Both
              </button>
            </div>
          </div>

          {/* Category Filter Bar */}
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-[var(--text-secondary)]">Filter by category:</span>

            <button
              onClick={() => setCategoryFilter(null)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                !categoryFilter
                  ? 'bg-[var(--origin-blue-500)] text-white'
                  : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
              }`}
            >
              All ({sortedAndFilteredData.length})
            </button>

            {analysisResult?.n_consensus > 0 && (
              <button
                onClick={() => setCategoryFilter('CONSENSUS')}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  categoryFilter === 'CONSENSUS'
                    ? 'bg-green-500 text-white'
                    : 'bg-green-900/20 text-green-400 hover:bg-green-900/30'
                }`}
              >
                <CheckCircle2 className="w-3 h-3" />
                Consensus ({analysisResult.n_consensus})
              </button>
            )}

            {analysisResult?.n_top_expression > 0 && (
              <button
                onClick={() => setCategoryFilter('TOP_EXPRESSION')}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  categoryFilter === 'TOP_EXPRESSION'
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-900/20 text-blue-400 hover:bg-blue-100/10'
                }`}
              >
                <TrendingUp className="w-3 h-3" />
                Expression ({analysisResult.n_top_expression})
              </button>
            )}

            {analysisResult?.n_top_network > 0 && (
              <button
                onClick={() => setCategoryFilter('TOP_NETWORK')}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  categoryFilter === 'TOP_NETWORK'
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-900/20 text-purple-400 hover:bg-purple-900/30'
                }`}
              >
                <Network className="w-3 h-3" />
                Network ({analysisResult.n_top_network})
              </button>
            )}

            {analysisResult?.n_divergent > 0 && (
              <button
                onClick={() => setCategoryFilter('DIVERGENT')}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  categoryFilter === 'DIVERGENT'
                    ? 'bg-orange-500 text-white'
                    : 'bg-orange-900/20 text-orange-400 hover:bg-orange-900/30'
                }`}
              >
                <AlertCircle className="w-3 h-3" />
                Divergent ({analysisResult.n_divergent})
              </button>
            )}
          </div>
        </>
      )}

      {/* Results Count */}
      <div className="mb-4 text-sm text-[var(--text-secondary)]">
        Showing {paginatedData.length} of {sortedAndFilteredData.length} results
        {searchQuery && ` (filtered from ${(hasDualRanking ? analysisResult?.targets?.length : analysisResult?.lncrna_results?.length) || 0} total)`}
      </div>

      {/* Table */}
      <div className="origin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-secondary)]">
                <th className="text-left p-3">
                  <button
                    onClick={() => handleSort('lncrna')}
                    className="flex items-center gap-2 font-semibold text-[var(--text-primary)] hover:text-[var(--origin-blue-500)] transition-colors"
                  >
                    lncRNA
                    <SortIcon field="lncrna" />
                  </button>
                </th>

                {/* Category column (dual ranking only) */}
                {hasDualRanking && (
                  <th className="text-center p-3">
                    <span className="font-semibold text-[var(--text-primary)]">
                      Category
                    </span>
                  </th>
                )}

                {/* Expression Rank column */}
                {hasDualRanking ? (
                  (viewMode === 'expression' || viewMode === 'both') && (
                    <th className="text-right p-3">
                      <button
                        onClick={() => handleSort('expression_rank')}
                        className="flex items-center gap-2 ml-auto font-semibold text-[var(--text-primary)] hover:text-[var(--origin-blue-500)] transition-colors"
                      >
                        Expression Rank
                        <SortIcon field="expression_rank" />
                      </button>
                    </th>
                  )
                ) : (
                  <th className="text-right p-3">
                    <button
                      onClick={() => handleSort('rank')}
                      className="flex items-center gap-2 ml-auto font-semibold text-[var(--text-primary)] hover:text-[var(--origin-blue-500)] transition-colors"
                    >
                      Rank
                      <SortIcon field="rank" />
                    </button>
                  </th>
                )}

                {/* Priority Rank column (dual ranking only) */}
                {hasDualRanking && (viewMode === 'network' || viewMode === 'both') && (
                  <th className="text-right p-3">
                    <button
                      onClick={() => handleSort('priority_rank')}
                      className="flex items-center gap-2 ml-auto font-semibold text-purple-400 hover:text-purple-700 transition-colors"
                      title="Priority Rank: Multi-factor therapeutic priority (60% network, 20% specificity, 10% druggability, 5% literature, 5% expression)"
                    >
                      Priority Rank
                      <SortIcon field="priority_rank" />
                    </button>
                  </th>
                )}

                {/* Rank Difference column (dual ranking 'both' mode only) */}
                {hasDualRanking && viewMode === 'both' && (
                  <th className="text-right p-3">
                    <button
                      onClick={() => handleSort('rank_difference')}
                      className="flex items-center gap-2 ml-auto font-semibold text-[var(--text-primary)] hover:text-[var(--origin-blue-500)] transition-colors"
                    >
                      Δ
                      <SortIcon field="rank_difference" />
                    </button>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row: any, idx: number) => (
                <tr
                  key={idx}
                  onClick={() => setSelectedTarget(row.lncrna)}
                  className="border-b border-[var(--border-primary)] hover:bg-[var(--bg-secondary)] transition-colors cursor-pointer"
                  title="Click to view detailed analysis"
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[var(--origin-blue-500)] hover:underline">
                        {row.lncrna}
                      </span>
                      {row.in_grn ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-900/20 text-green-400 border border-green-800 rounded-full"
                          title="Precomputed GRN available - instant loading"
                        >
                          <Network className="w-3 h-3" />
                          GRN
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-full"
                          title="GRN can be generated on-demand using correlation analysis (5-15 seconds)"
                        >
                          <Zap className="w-3 h-3" />
                          Generate
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Category badge (dual ranking only) */}
                  {hasDualRanking && (
                    <td className="p-3 text-center">
                      <RankingCategoryBadge category={row.ranking_category} size="sm" showLabel={false} />
                    </td>
                  )}

                  {/* Expression Rank or single Rank */}
                  {hasDualRanking ? (
                    (viewMode === 'expression' || viewMode === 'both') && (
                      <td className="p-3 text-right">
                        <span className="font-semibold text-[var(--origin-blue-500)]">
                          #{row.expression_rank}
                        </span>
                      </td>
                    )
                  ) : (
                    <td className="p-3 text-right">
                      <span className="font-semibold text-[var(--origin-blue-500)]">
                        {row.rank ?? 'N/A'}
                      </span>
                    </td>
                  )}

                  {/* Priority Rank (dual ranking only) */}
                  {hasDualRanking && (viewMode === 'network' || viewMode === 'both') && (
                    <td className="p-3 text-right">
                      <span className="font-semibold text-purple-400">
                        #{row.priority_rank}
                      </span>
                    </td>
                  )}

                  {/* Rank Difference (dual ranking 'both' mode only) */}
                  {hasDualRanking && viewMode === 'both' && (
                    <td className="p-3 text-right">
                      <span className="text-sm text-[var(--text-secondary)]">
                        {row.rank_difference}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="origin-btn origin-btn-ghost disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-[var(--origin-blue-500)] text-white'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            {totalPages > 5 && (
              <>
                <span className="text-[var(--text-tertiary)]">...</span>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    currentPage === totalPages
                      ? 'bg-[var(--origin-blue-500)] text-white'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
                  }`}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="origin-btn origin-btn-ghost disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
      </div>
    </>
  );
};

export default TableTab;
